import React, { useState } from 'react';
import { calculateCCRS, getStatus, PARAM_LABELS } from '../../utils/ccrsCalculator';
import { weatherService } from '../../services/weatherService';
import './CCRSCalculator.css';

const CCRSCalculator = () => {
  const [crop, setCrop] = useState('Jagung');
  const [inputs, setInputs] = useState({
    Rain: '0',
    Wind: '0',
    Humidity: '0',
    Visibility: '0',
    Pressure: '0',
    UV: '0',
    DewPoint: '0',
    SoilMoisture: '82',
    pH: '6'
  });
  const [result, setResult] = useState(null);
  const [location, setLocation] = useState('');
  const [weatherInfo, setWeatherInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);

  const handleInputChange = (param, value) => {
    setInputs({
      ...inputs,
      [param]: value
    });
  };

  const handleCalculate = () => {
    const ccrs = calculateCCRS(crop, inputs);
    const status = getStatus(ccrs);
    setResult({ ccrs: ccrs.toFixed(1), status });
  };

  const handleReset = () => {
    setInputs({
      Rain: '',
      Wind: '',
      Humidity: '',
      Visibility: '',
      Pressure: '',
      UV: '',
      DewPoint: '',
      SoilMoisture: '',
      pH: ''
    });
    setResult(null);
    setWeatherInfo(null);
    setError(null);
    setDataFetched(false);
    setLocation('');
  };

  const handleFetchWeather = async () => {
    if (!location.trim()) {
      setError('Masukkan nama kota terlebih dahulu');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const weatherData = await weatherService.getWeatherByCity(location.trim());
      
      // Update inputs dengan data cuaca
      setInputs({
        Rain: weatherData.Rain.toString(),
        Wind: weatherData.Wind.toString(),
        Humidity: weatherData.Humidity.toString(),
        Visibility: weatherData.Visibility.toString(),
        Pressure: weatherData.Pressure.toString(),
        UV: weatherData.UV.toString(),
        DewPoint: weatherData.DewPoint.toString(),
        SoilMoisture: inputs.SoilMoisture,
        pH: inputs.pH
      });

      setWeatherInfo({
        location: weatherData.location,
        country: weatherData.country,
        description: weatherData.description,
        temp: weatherData.temp,
        icon: weatherData.icon
      });

      setDataFetched(true);
      setError(null);
    } catch (err) {
      setError(err.message || 'Gagal mengambil data cuaca. Pastikan nama kota benar.');
      setWeatherInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUseCurrentLocation = async () => {
    setLoading(true);
    setError(null);

    try {
      const coords = await weatherService.getCurrentLocation();
      const weatherData = await weatherService.getWeatherByCoordinates(coords.lat, coords.lon);
      
      // Update inputs dengan data cuaca
      setInputs({
        Rain: weatherData.Rain.toString(),
        Wind: weatherData.Wind.toString(),
        Humidity: weatherData.Humidity.toString(),
        Visibility: weatherData.Visibility.toString(),
        Pressure: weatherData.Pressure.toString(),
        UV: weatherData.UV.toString(),
        DewPoint: weatherData.DewPoint.toString(),
        SoilMoisture: inputs.SoilMoisture,
        pH: inputs.pH
      });

      setWeatherInfo({
        location: weatherData.location,
        country: weatherData.country,
        description: weatherData.description,
        temp: weatherData.temp,
        icon: weatherData.icon
      });

      setLocation(weatherData.location);
      setDataFetched(true);
      setError(null);
    } catch (err) {
      setError(err.message || 'Gagal mendapatkan lokasi Anda');
      setWeatherInfo(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ccrs-calculator">
      <div className="container">
        <header className="calculator-header">
          <h1>🌾 Kalkulator CCRS</h1>
          <p>Climate Crop Risk Score - Analisis Kelayakan Tanam</p>
        </header>

        <div className="calculator-grid">
          {/* Input Section */}
          <div className="card input-section">
            <h2 className="card-header">📝 Input Parameter</h2>
            
            {/* Crop Selection */}
            <div className="form-group">
              <label className="form-label">Pilih Tanaman</label>
              <select 
                className="form-control"
                value={crop}
                onChange={(e) => setCrop(e.target.value)}
              >
                <option value="Padi">Padi</option>
                <option value="Jagung">Jagung</option>
              </select>
            </div>

            <hr />

            {/* Weather Location Section */}
            <div className="weather-section">
              <h3 className="section-title">🌍 Ambil Data Cuaca Otomatis</h3>
              
              <div className="location-input-group">
                <input
                  type="text"
                  className="form-control"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Masukkan nama kota (contoh: Jakarta, Bandung)"
                  onKeyPress={(e) => e.key === 'Enter' && handleFetchWeather()}
                />
                <button 
                  className="btn btn-weather"
                  onClick={handleFetchWeather}
                  disabled={loading}
                >
                  {loading ? '⏳' : '🌤️'} Ambil Data
                </button>
              </div>

              <button 
                className="btn btn-location"
                onClick={handleUseCurrentLocation}
                disabled={loading}
              >
                📍 Gunakan Lokasi Saat Ini
              </button>

              {error && (
                <div className="alert alert-error">
                  ⚠️ {error}
                </div>
              )}

              {weatherInfo && (
                <div className="weather-info">
                  <div className="weather-header">
                    <img 
                      src={`https://openweathermap.org/img/wn/${weatherInfo.icon}@2x.png`}
                      alt={weatherInfo.description}
                      className="weather-icon"
                    />
                    <div>
                      <div className="weather-location">
                        {weatherInfo.location}, {weatherInfo.country}
                      </div>
                      <div className="weather-temp">{weatherInfo.temp}°C</div>
                      <div className="weather-desc">{weatherInfo.description}</div>
                    </div>
                  </div>
                  <div className="weather-note">
                    ✅ Data cuaca berhasil diambil! Soil Moisture dan pH perlu diinput manual.
                  </div>
                </div>
              )}
            </div>

            <hr />

            {/* Auto-fetched Weather Parameters */}
            <h3 className="section-title">🌤️ Data Cuaca Otomatis</h3>
            {['Rain', 'Wind', 'Humidity', 'Visibility', 'Pressure', 'UV', 'DewPoint'].map(param => (
              <div key={param} className="form-group">
                <label className="form-label">{PARAM_LABELS[param]}</label>
                <input
                  type="number"
                  step="0.1"
                  className={`form-control ${dataFetched ? 'readonly-field' : ''}`}
                  value={inputs[param]}
                  onChange={(e) => !dataFetched && handleInputChange(param, e.target.value)}
                  placeholder={dataFetched ? 'Otomatis dari API' : `Masukkan ${PARAM_LABELS[param]}`}
                  readOnly={dataFetched}
                />
              </div>
            ))}

            <hr />

            {/* Manual Input Parameters */}
            <h3 className="section-title">🌱 Data Tanah (Input Manual)</h3>
            {['SoilMoisture', 'pH'].map(param => (
              <div key={param} className="form-group">
                <label className="form-label">{PARAM_LABELS[param]}</label>
                <input
                  type="number"
                  step="0.1"
                  className="form-control manual-field"
                  value={inputs[param]}
                  onChange={(e) => handleInputChange(param, e.target.value)}
                  placeholder={`Masukkan ${PARAM_LABELS[param]}`}
                />
              </div>
            ))}

            {/* Buttons */}
            <div className="button-group">
              <button 
                className="btn btn-primary btn-block"
                onClick={handleCalculate}
              >
                🔍 Hitung CCRS
              </button>
              <button 
                className="btn btn-secondary btn-block"
                onClick={handleReset}
              >
                🔄 Reset
              </button>
            </div>
          </div>

          {/* Result Section */}
          <div className="result-section">
            <div className="card result-card">
              <h2 className="card-header">📊 Hasil Analisis</h2>
              
              {result ? (
                <>
                  <div className="result-item">
                    <span className="result-label">Tanaman:</span>
                    <span className="result-value crop-name">{crop}</span>
                  </div>

                  <div className="result-item ccrs-result">
                    <span className="result-label">CCRS:</span>
                    <span className="result-value ccrs-value">{result.ccrs}</span>
                  </div>

                  <div className="status-badge-container">
                    <div 
                      className="status-badge-large"
                      style={{ backgroundColor: result.status.color }}
                    >
                      {result.status.text}
                    </div>
                  </div>

                  <div className="result-details">
                    <h3>Parameter Input:</h3>
                    <div className="param-list">
                      {Object.keys(inputs).map(param => (
                        inputs[param] && (
                          <div key={param} className="param-item">
                            <span className="param-name">{PARAM_LABELS[param]}:</span>
                            <span className="param-value">{inputs[param]}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-result">
                  <div className="no-result-icon">📈</div>
                  <p>Isi parameter dan klik tombol "Hitung CCRS"</p>
                  <p className="hint">💡 Gunakan fitur ambil data cuaca otomatis untuk mempermudah input!</p>
                </div>
              )}
            </div>

            {/* Legend Section */}
            <div className="card legend-card">
              <h3 className="card-header">📋 Interpretasi Status</h3>
              <div className="legend-list">
                <div className="legend-item">
                  <div className="legend-badge" style={{ backgroundColor: '#48bb78' }}>
                    Sangat Layak
                  </div>
                  <span className="legend-range">CCRS &gt; 85</span>
                </div>
                <div className="legend-item">
                  <div className="legend-badge" style={{ backgroundColor: '#4299e1' }}>
                    Layak
                  </div>
                  <span className="legend-range">70 - 85</span>
                </div>
                <div className="legend-item">
                  <div className="legend-badge" style={{ backgroundColor: '#ed8936' }}>
                    Perlu Perhatian
                  </div>
                  <span className="legend-range">55 - 70</span>
                </div>
                <div className="legend-item">
                  <div className="legend-badge" style={{ backgroundColor: '#f56565' }}>
                    Tunda Tanam
                  </div>
                  <span className="legend-range">CCRS &lt; 55</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CCRSCalculator;
